import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiFailureError } from '../helpers/api-error.util';
import { AvailableWork, InterestWithdrawalResult } from '../model/available-work';

/**
 * Standard connect-service response envelope. Confirmed against
 * `StaffingNeedResource.getAvailableWork`: `GET /staffing-need` returns
 * `ApiResponse<List<AvailableStaffingNeed>>` on success, and the same
 * envelope with `status: FAILURE` and `data: ""` when the principal cannot
 * be resolved or an unexpected exception occurs — never a bare array and
 * never a raw error object.
 */
interface ApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: T;
  error?: string;
}

/**
 * Vendor-facing Available Work feed client (CONNECT 2.0 work map, "Available
 * Work" tab).
 *
 * Session auth (cookie) and the CSRF header are layered on by the root
 * `authInterceptor` — mirrors `ProjectService.fetch`/`DeviceService`, which
 * set no manual `Authorization` header and do not touch CSRF themselves. That
 * matters for the two writes below and not for `fetch()`: `POST` and `DELETE`
 * are in the interceptor's `STATE_CHANGING_METHODS`, so it echoes the
 * `connect_csrf_token` cookie as `X-CSRF-Token` on both, while a `GET` needs
 * no such header. Setting one by hand here would duplicate that work and
 * could disagree with the cookie after a rotation.
 *
 * Both writes are principal-resolved and carry NO userId — see each method.
 */
@Injectable({
  providedIn: 'root',
})
export class AvailableWorkService {
  constructor(private http: HttpClient) {}

  /**
   * connect-service answers a FAILURE envelope with HTTP 200 (the real
   * outcome lives in the envelope's `status`/`code`, not the wire status),
   * so an RxJS `error` callback never fires on its own. Throws an
   * `ApiFailureError` on a FAILURE/4xx+ envelope so a caller's `error`
   * handler runs instead of treating a failure as an empty feed. Mirrors
   * `AliasService.throwIfFailure`, including its `ApiFailureError` type —
   * that is what marks the message as server-authored and safe to show via
   * `serverErrorMessage`.
   */
  private throwIfFailure(r: {
    status?: string;
    code?: number;
    message?: string;
    error?: string;
  }): void {
    if (
      r &&
      (r.status === 'FAILURE' || (typeof r.code === 'number' && r.code >= 400))
    ) {
      throw new ApiFailureError(r.message || r.error || 'Request failed', r.code);
    }
  }

  /**
   * The acting vendor's Available Work feed: every `OPEN` staffing need they
   * qualify for and are not already engaged with, each carrying whether
   * they've already expressed interest. See
   * `StaffingNeedHandler.getAvailableWork` for the eligibility rule — this
   * service applies none of it; it only unwraps the envelope. Throws on a
   * FAILURE envelope rather than emitting one, so a genuinely failed fetch
   * is never mistaken for "nothing available" (see Task 7/8's distinct
   * empty-vs-failed states).
   */
  fetch(): Observable<AvailableWork[]> {
    return this.http
      .get<ApiResponse<AvailableWork[]>>(environment.apiUrl + '/staffing-need')
      .pipe(
        map((r) => {
          this.throwIfFailure(r);
          return Array.isArray(r?.data) ? r.data : [];
        })
      );
  }

  /**
   * Register the acting vendor's interest in one open need.
   *
   * The route carries NO userId — connect-service resolves the acting vendor
   * from the authenticated principal (see `StaffingNeedResource.expressInterest`),
   * and its javadoc says not to add one. Do not send one here either.
   *
   * Answers nothing useful on success, so this emits `void`: the outcome the
   * caller renders is the resulting card state, not a payload. The FAILURE
   * envelopes DO carry operator-facing copy worth showing — "This work has
   * already been staffed." / "This work is no longer available." — which is
   * why `throwIfFailure` runs here too: without it, an HTTP-200 FAILURE would
   * reach the `next` handler and a card would commit an interest the server
   * refused.
   */
  expressInterest(id: string): Observable<void> {
    return this.http
      .post<ApiResponse<unknown>>(
        `${environment.apiUrl}/staffing-need/${id}/interest`,
        // No body — the need id is on the path and the vendor is the principal,
        // so there is nothing left to send. `null` (not `{}`) keeps an empty
        // JSON object off the wire.
        null
      )
      .pipe(map((r) => this.throwIfFailure(r)));
  }

  /**
   * Withdraw the acting vendor's interest in one need — the inverse of
   * {@link expressInterest}, on the same path. Principal-resolved, no userId:
   * `StaffingNeedResource.withdrawInterest`'s javadoc is explicit that its
   * absence is what closes the horizontal-IDOR shape.
   *
   * <b>The success message is deliberately not returned.</b> connect-service
   * answers "Your interest has been withdrawn." on EVERY branch — including
   * when the vendor never had interest to withdraw, and when the need is
   * closed or unknown (uniform by design, so no answer is an existence
   * oracle). That makes it true about the end state and false about what
   * happened, so it must not be echoed to the vendor.
   *
   * <b>What IS returned is the envelope's `data.removed`</b> — the one
   * machine-readable fact the uniform answer carries (see
   * `StaffingNeedHandler.InterestWithdrawalResult`). It is `true` only when
   * this caller's own interest was the thing removed, so it is the only signal
   * a caller has that a withdrawal actually happened; without it, a card
   * commits "Express Interest" on a withdrawal the `status = OPEN` guard
   * refused. It is `false` for all four no-op causes and distinguishes none of
   * them from each other, so a caller can act on "not confirmed" but must not
   * infer a reason.
   *
   * @returns true when this caller's interest was removed, false when nothing
   *          was.
   */
  withdrawInterest(id: string): Observable<boolean> {
    return this.http
      .delete<ApiResponse<InterestWithdrawalResult | string | null>>(
        `${environment.apiUrl}/staffing-need/${id}/interest`
      )
      .pipe(
        map((r) => {
          this.throwIfFailure(r);
          // `=== true`, not a truthy read, and deliberately conservative: an
          // absent or non-object `data` (the `""` a FAILURE envelope carries,
          // or a connect-service build from before this flag existed) reads as
          // "not confirmed" and sends the caller down its re-fetch path. That
          // degrades to an extra fetch, which is truthful; degrading the other
          // way would commit a state change the server may have refused, which
          // is the defect this flag exists to close.
          const data = r?.data;
          return (
            typeof data === 'object' &&
            data !== null &&
            (data as InterestWithdrawalResult).removed === true
          );
        })
      );
  }
}
